import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import AppButton from '../../../../components/designe/app-button/AppButton';
import ConfirmDialog from '../../../../components/reusable/confirm-dialog/ConfirmDialog';
import FlexibleColumnsTable from '../../../../components/reusable/Table/FlexibleColumnsTable';
import Search from '../../../../components/reusable/search/Search';
import { useAccess } from '../../../../hooks/useAccess';
import { useGetLanguages } from '../../../../services/redux/api/languagesAPI';
import { useGetNotificationCampaignsQuery } from '../../../../services/redux/api/notificationsAdminApi';
import theme from '../../../../styles/theme';
import { useNotificationsTableStyles } from '../NotificationsTable.styles';
import { notificationsBasePath } from '../notificationsBasePath';
import { useNotificationCampaignsListComponent } from './useNotificationCampaignsListComponent';

export function NotificationCampaignsList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { classes } = useNotificationsTableStyles(theme);
  const { access } = useAccess();
  const canMutate =
    (access?.notifications?.canCreate || access?.notifications?.canEdit) ??
    false;
  const canDelete = access?.notifications?.canDelete ?? false;

  const { data: campaigns = [], isLoading } =
    useGetNotificationCampaignsQuery();
  const { data: languages = [] } = useGetLanguages();

  const langById = useMemo(() => {
    const m = new Map<string, string>();
    languages.forEach((l) => {
      if (l.id) m.set(l.id, l.title || l.key || l.id);
    });
    return m;
  }, [languages]);

  function onEdit(row: { id: string }) {
    navigate(`${notificationsBasePath}/campaigns/${row.id}`);
  }

  const {
    sortedArray,
    columnsList,
    order,
    orderBy,
    cellReducers,
    onSort,
    onSortColumns,
    filter,
    setFilter,
    itemForDelete,
    setItemForDelete,
    onItemDelete,
  } = useNotificationCampaignsListComponent(
    classes,
    campaigns,
    langById,
    canMutate,
    canDelete,
    onEdit,
  );

  const titleNode = useMemo(
    () => (
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}>
        <Typography variant="h6">
          {t('adminDashboard.notifications.campaigns.title')}
        </Typography>
        {canMutate && (
          <AppButton
            onClick={() => navigate(`${notificationsBasePath}/campaigns/new`)}>
            {t('common.add')}
          </AppButton>
        )}
      </Box>
    ),
    [canMutate, navigate, t],
  );

  if (isLoading) {
    return (
      <div className={classes.wrapper}>
        {titleNode}
        <Typography>{t('adminDashboard.notifications.loading')}</Typography>
      </div>
    );
  }

  return (
    <div className={classes.wrapper}>
      {titleNode}
      <Box display="flex" gap={1} alignItems="center" marginBottom={2}>
        <Search fullWidth value={filter} onChange={setFilter} />
      </Box>
      <FlexibleColumnsTable
        data={sortedArray}
        columnsList={columnsList}
        order={order}
        orderBy={orderBy}
        onSort={onSort}
        classes={classes}
        onSortColumns={onSortColumns}
        cellReducers={cellReducers}
        onSelectAllRows={() => {}}
        stickyHeader={true}
      />
      <ConfirmDialog
        open={!!itemForDelete}
        onClose={() => setItemForDelete(null)}
        title={t('common.delete')}
        onSubmit={() => onItemDelete()}>
        {t('common.deleteQuestion')}
      </ConfirmDialog>
    </div>
  );
}
